import React, { useRef, useState, useEffect } from "react";
import emailjs from "@emailjs/browser";
import "../styles/ContactUs.css";
import { useLanguage } from "../contexts/LanguageContext";
import { track } from "../utils/analytics";

const ContactUs = () => {
  const { language, text } = useLanguage();
  const isHeb = language === "he";
  const c = text.contact;
  const bookingUrl = text.contactDetails.bookingUrl;
  const formRef = useRef();
  const mountedAtRef = useRef(Date.now());
  const [statusMessage, setStatusMessage] = useState("");
  const [statusType, setStatusType] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (process.env.REACT_APP_EMAILJS_PUBLIC_KEY) {
      emailjs.init(process.env.REACT_APP_EMAILJS_PUBLIC_KEY);
    }
    mountedAtRef.current = Date.now();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatusMessage("");

    const data = new FormData(formRef.current);
    const payload = {
      name: data.get("name") || "",
      email: data.get("email") || "",
      message: data.get("message") || "",
      project_type: data.get("project_type") || "",
      budget: data.get("budget") || "",
      timeline: data.get("timeline") || "",
      stage: data.get("stage") || "",
      website: data.get("website") || "",
      elapsedMs: Date.now() - mountedAtRef.current,
    };

    try {
      const res = await fetch("/.netlify/functions/validate-contact", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload),
      });
      const verdict = await res.json().catch(() => ({ ok: true }));

      if (!verdict.ok) {
        setStatusType("error");
        setStatusMessage(c.form.spamDetected);
        track("contact_form_spam_blocked", { reason: verdict.reason || "unknown" });
        setIsSubmitting(false);
        return;
      }
    } catch (err) {
      // Validation endpoint unreachable — fail open so legit users aren't blocked.
      console.warn("Spam check unreachable, allowing submission:", err);
    }

    emailjs
      .sendForm(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        formRef.current
      )
      .then(
        () => {
          setStatusType("success");
          setStatusMessage(c.form.success);
          track("contact_form_submit", {
            project_type: payload.project_type,
            budget: payload.budget,
            timeline: payload.timeline,
            stage: payload.stage,
          });
          formRef.current.reset();
          mountedAtRef.current = Date.now();
        },
        (error) => {
          console.error("EmailJS Error:", error);
          setStatusType("error");
          setStatusMessage(c.form.error);
          track("contact_form_error");
        }
      )
      .finally(() => setIsSubmitting(false));
  };

  return (
    <section id="contact" className="contact-section" dir={isHeb ? "rtl" : "ltr"}>
      <span className="eyebrow">{c.eyebrow}</span>
      <h2 className="section-title">{c.title}</h2>
      <p className="contact-lede">{c.lede}</p>

      {bookingUrl && (
        <div className="contact-book">
          <a
            className="btn btn-primary contact-book-btn"
            href={bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track("booking_click")}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" />
              <path d="M16 2v4M8 2v4M3 10h18" strokeLinecap="round" />
            </svg>
            {c.bookCta}
          </a>
          <div className="contact-divider">
            <span>{c.orDivider}</span>
          </div>
        </div>
      )}

      <form className="contact-form" onSubmit={handleSubmit} ref={formRef}>
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            left: "-10000px",
            top: "auto",
            width: "1px",
            height: "1px",
            overflow: "hidden",
          }}
        >
          <label>
            Website (leave blank)
            <input
              type="text"
              name="website"
              tabIndex="-1"
              autoComplete="off"
            />
          </label>
        </div>

        <div className="field-row">
          <label className="field">
            <span className="field-label">{c.form.name}</span>
            <input
              dir={isHeb ? "rtl" : "ltr"}
              type="text"
              name="name"
              placeholder={c.form.name}
              required
            />
          </label>

          <label className="field">
            <span className="field-label">{c.form.email}</span>
            <input
              dir={isHeb ? "rtl" : "ltr"}
              type="email"
              name="email"
              placeholder={c.form.email}
              required
            />
          </label>
        </div>

        <div className="field-row">
          <label className="field">
            <span className="field-label">{c.form.projectType}</span>
            <select
              name="project_type"
              required
              dir={isHeb ? "rtl" : "ltr"}
              defaultValue=""
            >
              {c.form.projectTypeOptions.map((o) => (
                <option key={o.value} value={o.value} disabled={o.value === ""}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span className="field-label">{c.form.budget}</span>
            <select
              name="budget"
              required
              dir={isHeb ? "rtl" : "ltr"}
              defaultValue=""
            >
              {c.form.budgetOptions.map((o) => (
                <option key={o.value} value={o.value} disabled={o.value === ""}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="field-row">
          <label className="field">
            <span className="field-label">{c.form.timeline}</span>
            <select
              name="timeline"
              required
              dir={isHeb ? "rtl" : "ltr"}
              defaultValue=""
            >
              {c.form.timelineOptions.map((o) => (
                <option key={o.value} value={o.value} disabled={o.value === ""}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span className="field-label">{c.form.stage}</span>
            <select
              name="stage"
              dir={isHeb ? "rtl" : "ltr"}
              defaultValue=""
            >
              {c.form.stageOptions.map((o) => (
                <option key={o.value} value={o.value} disabled={o.value === ""}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className="field">
          <span className="field-label">{c.form.message}</span>
          <textarea
            dir={isHeb ? "rtl" : "ltr"}
            name="message"
            placeholder={c.form.message}
            rows="4"
          />
        </label>

        <button
          type="submit"
          className="btn btn-primary contact-submit"
          disabled={isSubmitting}
        >
          {isSubmitting ? c.form.sending : c.form.submit}
          {!isSubmitting && (
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 2L11 13" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M22 2l-7 20-4-9-9-4 20-7z" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </button>

        {statusMessage && (
          <p className={`status-message status-${statusType}`}>{statusMessage}</p>
        )}
      </form>
    </section>
  );
};

export default ContactUs;
